import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { StatusLabel, Text } from 'components/Text';
import { LoadingScreen } from 'components/LoadingScreen';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { GET_CLAIM } from 'gql/queries';
import { DownloadIcon } from '@heroicons/react/outline';
import {
  DENY_CLAIM,
  REFUND_CLAIM,
  REORDER_CLAIM,
  INSERT_CLAIM_IMAGES,
  REMOVE_CLAIM_IMAGE,
  OTHER_CLAIM,
  STORE_CREDIT_CLAIM,
  INSERT_CLAIM_VIDEOS,
  REMOVE_CLAIM_VIDEO,
  DESTROY_CLAIM,
} from 'gql/mutations';

import {
  Claim,
  ClaimItemResolvedStatusEnum,
  ClaimItemValues,
  statusType,
} from 'types/claim';
import { Order } from 'types/order';
import { ClaimsImagesUpload } from 'components/ClaimsImagesUpload';
import { Reorder } from './Reorder';
import { Refund } from './Refund';
import { Other } from './Other';
import { Denyclaim } from './DenyClaim';
import { format } from 'date-fns';
import { useStore } from 'context/store-context';
import { ResolutionRefund } from 'components/ResolutionRefund';
import { ResolutionDeny } from 'components/ResolutionDeny';
import { ResolutionReorder } from 'components/ResolutionReorder';
import { ResolutionOther } from 'components/ResolutionOther';
import { Back } from 'components/Back';
import {
  formatCurrency,
  getStoreCurrencyFormatter,
  apiErrorHandler,
  formatStatusText,
  getThumnailUrl,
} from 'utils';
import { withNotification } from 'components/Notification';
import { NotificationTypes } from 'utils';
import { Modal } from 'components/Modal';
import ClaimItemsList from 'components/ClaimItemsList';
import { Dropdown } from 'components/Dropdown';
import { StoreCredit } from './StoreCredit';
import { ResolutionStoreCredit } from 'components/ResolutionStoreCredit';
import { ClaimsVideosUpload } from 'components/ClaimsVideosUpload';
import { ViewVideo } from './ViewVideo';
import OrderRiskAnalysisResult from 'components/OrderRiskAnalysis/OrderRiskAnalysisResult';
import ActivitiesTimeline from 'components/OrderActivitiesTimeline';

import withConfirmation from 'hooks/withConfirmation';
import { ExternalLinkIcon, UserIcon } from '@heroicons/react/solid';
import { Button } from 'components/Button';
import { useUser } from 'context/user-context';
import { userRoles } from 'types/user';
import {
  getUniqueTrackingNumbers,
  TrackingLink,
} from 'components/TrackingLink';

import {
  getOrderFulfillmentStatus,
  getOrderFulfillmentStatusType,
} from 'utils/orderFulfillmentStatus';
import { OrderRequestsPanel } from 'components/OrderRequestsPanel';
import {
  getRequestReturnTo,
  getShopifyOrderAdminUrl,
  withRequestReturnTo,
} from 'utils/requestNavigation';

const resolvedStatuses = [
  'RESOLVED',
  'PARTIALLY_RESOLVED',
  'DENIED',
  'ARCHIVED',
];

export const statusColorClasses = (status: string | undefined) => {
  if (!status) {
    return '';
  }
  switch (status) {
    case statusType.new:
      return 'text-blue-700 bg-blue-100';
    case statusType.in_review:
      return 'text-yellow-700 bg-yellow-100';
    case statusType.denied:
      return 'text-red-700 bg-red-100';
    case statusType.partially_resolved:
      return 'text-yellow-700 bg-yellow-100';
    case statusType.resolved:
      return 'bg-green-100 text-green-700';
    default:
      return 'text-yellow-700 bg-yellow-100';
  }
};

export const ClaimResolvedStatusBadges = ({
  status,
  customCss = '',
}: {
  status: string | undefined;
  customCss?: string;
}) => {
  let textAndBgColorCss = '';

  if (!status) {
    return null;
  }
  switch (status) {
    case ClaimItemResolvedStatusEnum.DENY:
      textAndBgColorCss = 'text-red-700 bg-red-100';
      break;
    default:
      textAndBgColorCss = 'text-blue-700 bg-blue-100';
  }
  return (
    <Text
      value={formatStatusText(status)}
      type="bold"
      className={`px-3 py-1 rounded text-center ${textAndBgColorCss} ${customCss}`}
    />
  );
};

export const ClaimResolutionOptions = (settings: {
  enableStoreCredit: boolean;
}) =>
  Object.values(ClaimItemResolvedStatusEnum)
    .filter(
      (i) =>
        ![
          ClaimItemResolvedStatusEnum.IN_REVIEW,
          !settings.enableStoreCredit
            ? ClaimItemResolvedStatusEnum.STORE_CREDIT
            : '',
        ].includes(i),
    )
    .map((key) => ({
      key,
      render: (
        <div className="px-2 py-1">
          <ClaimResolvedStatusBadges status={key} customCss="px-2" />
        </div>
      ),
    }));

const ClaimResolutionModalHeader = ({
  claim,
  claimItem,
  resolution,
  storeProperties,
}: {
  resolution?: any;
  claim: any;
  claimItem?: any;
  storeProperties: any;
}) => {
  return (
    <section className="mt-8">
      <div>
        <Text
          value={`Resolution`}
          type="h1"
          className="font-normal text-blue-700"
        />

        {!claimItem ? (
          <div className="flex justify-start items-center flex-wrap">
            <Text
              value={'Proceed to resolve this item/s as'}
              type="body"
              className="text-gray-500 font-normal mr-2"
            />
            <ClaimResolvedStatusBadges status={resolution} />
          </div>
        ) : (
          <Text
            value={'See issue resolution details below for the above item'}
            type="body"
            className="text-gray-500 font-normal"
          />
        )}
      </div>
      {storeProperties?.resolvePortalOptions?.requirePreferredResolution ? (
        <div>
          <div>
            <Text
              value={`Preferred Resolution: ${
                formatStatusText(claim?.preferredResolution) ?? 'N/A'
              }`}
              type="body"
              className="font-semibold text-gray-700 my-2"
            />
          </div>
          {claim?.preferredResolution === 'OTHER' ? (
            <div>
              <textarea
                rows={1}
                className="border border-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-full sm:text-sm border-gray-300 rounded-md mr-3 p-1"
                value={claim?.preferredResolutionNotes ?? ''}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export const SingleClaim = withConfirmation(
  withNotification(({ showNotification, setShowConfirmation }: any) => {
    const user = useUser();

    const [claim, setClaim] = useState<Claim | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [image, setImage] = useState(null);
    const [video, setVideo] = useState<string | null>(null);
    const [selectedClaimItems, setSelectedClaimItems] = useState<
      ClaimItemValues[]
    >([]);

    const [currentClaimItem, setCurrentClaimItem] =
      useState<ClaimItemValues | null>(null);

    const [resolution, setResolution] = useState<
      ClaimItemResolvedStatusEnum | undefined
    >();

    const [resolutionActionModal, setResolutionActionModal] = useState(false);
    const [resolutionViewModal, setResolutionViewModal] = useState(false);

    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const returnTo = getRequestReturnTo(
      location.search,
      (location.state as { returnLocation?: string } | null)?.returnLocation,
      '/requests',
    );

    const { storeId, storeProperties, storeDomain } = useStore();
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [videoUrls, setVideoUrls] = useState<string[]>([]);

    const enableStoreCredit =
      storeProperties?.resolvePortalOptions?.enableStoreCredit || false;

    const { data, loading } = useQuery(GET_CLAIM, {
      variables: {
        storeId,
        id: params.claimId,
      },
      onError(error) {
        showNotification(NotificationTypes.error, error?.message);
      },
    });
    const [refetchClaim, { data: refetchData }] = useLazyQuery(GET_CLAIM);

    const [destroyClaim] = useMutation(DESTROY_CLAIM, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        showNotification(NotificationTypes.success, 'Issue deleted');

        return setTimeout(() => {
          return navigate(returnTo, { replace: true });
        }, 300);
      },
    });

    const [reorderClaim, { loading: reorderClaimLoading }] = useMutation(
      REORDER_CLAIM,
      {
        onError(error) {
          const newError = apiErrorHandler(error);
          showNotification(NotificationTypes.error, newError?.message);
        },
        onCompleted(data) {
          showNotification(NotificationTypes.success, 'Issue resolved');
          refetchClaim({
            fetchPolicy: 'cache-and-network',
            variables: {
              storeId,
              id: params.claimId,
            },
          });
          setResolutionViewModal(false);
          setResolutionActionModal(false);

          return window.open(data?.reorderClaim[0].orderUrl, '_blank');
        },
      },
    );

    const [refundClaim, { loading: refundClaimLoading }] = useMutation(
      REFUND_CLAIM,
      {
        onError(error) {
          const newError = apiErrorHandler(error);
          showNotification(NotificationTypes.error, newError?.message);
        },
        onCompleted(data) {
          showNotification(NotificationTypes.success, 'Issue resolved');
          refetchClaim({
            fetchPolicy: 'cache-and-network',
            variables: {
              storeId,
              id: params.claimId,
            },
          });
          setResolutionViewModal(false);
          setResolutionActionModal(false);
        },
      },
    );

    const [storeCreditClaim, { loading: storeCreditClaimLoading }] =
      useMutation(STORE_CREDIT_CLAIM, {
        onError(error) {
          const newError = apiErrorHandler(error);
          showNotification(NotificationTypes.error, newError?.message);
        },
        onCompleted(data) {
          showNotification(NotificationTypes.success, 'Issue resolved');
          refetchClaim({
            fetchPolicy: 'cache-and-network',
            variables: {
              storeId,
              id: params.claimId,
            },
          });
          setResolutionViewModal(false);
          setResolutionActionModal(false);
        },
      });

    const [otherClaim, { loading: otherClaimLoading }] = useMutation(
      OTHER_CLAIM,
      {
        onError(error) {
          const newError = apiErrorHandler(error);
          showNotification(NotificationTypes.error, newError?.message);
        },
        onCompleted(data) {
          showNotification(NotificationTypes.success, 'Issue resolved');
          refetchClaim({
            fetchPolicy: 'cache-and-network',
            variables: {
              storeId,
              id: params.claimId,
            },
          });
          setResolutionViewModal(false);
          setResolutionActionModal(false);
        },
      },
    );

    const [denyClaim, { loading: denyClaimLoading }] = useMutation(DENY_CLAIM, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        showNotification(NotificationTypes.success, 'Issue Denied');
        refetchClaim({
          fetchPolicy: 'cache-and-network',
          variables: {
            storeId,
            id: params.claimId,
          },
        });
        setResolutionViewModal(false);
        setResolutionActionModal(false);
      },
    });

    const [insertClaimImages] = useMutation(INSERT_CLAIM_IMAGES, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        setClaim(data?.insertClaimImages.returning[0].claim);
      },
    });

    const [insertClaimVideos] = useMutation(INSERT_CLAIM_VIDEOS, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        setClaim(data?.insertClaimVideos.returning[0].claim);
      },
    });

    const [removeClaimImage] = useMutation(REMOVE_CLAIM_IMAGE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        setClaim(data?.deleteClaimImage.claim);
      },
    });

    const [removeClaimVideo] = useMutation(REMOVE_CLAIM_VIDEO, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        setClaim(data?.deleteClaimVideo.claim);
      },
    });

    useEffect(() => {
      if (data?.claims) {
        setClaim(data.claims[0]);
      }
    }, [data]);

    useEffect(() => {
      if (claim) {
        const unResolvedItems = claim.items.filter((item) => {
          return !item.settlement;
        });

        setSelectedClaimItems(unResolvedItems);
      }
    }, [claim]);

    useEffect(() => {
      if (selectedClaimItems?.length) {
        setCurrentClaimItem(selectedClaimItems[0]);
      }
    }, [selectedClaimItems]);

    useEffect(() => {
      if (refetchData?.claims) {
        setClaim(refetchData.claims[0]);
        setSelectedClaimItems(refetchData.claims[0].items);
      }
    }, [refetchData]);

    useEffect(() => {
      if (image) {
        setShowImageModal(true);
      }
    }, [image]);

    const handleAddClaimImages = (urls: string[]) => {
      insertClaimImages({
        variables: {
          objects: urls.map((item) => ({
            claimId: claim?.id,
            url: item,
          })),
        },
      });
    };

    const handleAddClaimVideos = (urls: string[]) => {
      insertClaimVideos({
        variables: {
          objects: urls.map((item) => ({
            claimId: claim?.id,
            url: item,
          })),
        },
      });
    };

    const handleRemoveClaimItem = (orderItem: ClaimItemValues) => {
      if (selectedClaimItems.includes(orderItem)) {
        const filtered = selectedClaimItems.filter(
          (item) => item.id !== orderItem.id,
        );
        return setSelectedClaimItems(filtered);
      }
      setSelectedClaimItems([...selectedClaimItems, orderItem]);
    };

    const handleQuantityUpdate = (
      value: string,
      claimItem: ClaimItemValues,
    ) => {
      const updatedClaimItems = [...selectedClaimItems].map((item) =>
        item.id === claimItem.id ? { ...item, quantity: Number(value) } : item,
      );
      setSelectedClaimItems(updatedClaimItems);
    };

    const handleReorderClaim = (input: any) => {
      if (!currentClaimItem) {
        return alert('No item found');
      }
      let payload = {
        ...input,
        storeId,
        id: params.claimId,
        items: selectedClaimItems
          .map((item) => ({
            id: item.id,
            quantity: item.quantity,
          }))
          .filter((item) => item.quantity), // Filter items with 0 quantity,
      };
      if (!payload.items.length) {
        showNotification(NotificationTypes.error, 'Invalid Item Quantity');
      }
      reorderClaim({
        variables: { input: payload },
      });
    };

    const handleOtherClaims = (input: any) => {
      let payload = {
        ...input,
        storeId,
        id: params.claimId,
        items: selectedClaimItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
      };
      otherClaim({
        variables: { input: payload },
      });
    };

    const handleRefundClaim = (input: any) => {
      let payload = {
        ...input,
        storeId,
        id: params.claimId,
        items: selectedClaimItems
          .map((item) => ({
            id: item.id,
            quantity: item.quantity,
          }))
          .filter((item) => item.quantity), // Filter items with 0 quantity,
      };
      if (!payload.items.length) {
        showNotification(NotificationTypes.error, 'Invalid Item Quantity');
      }
      refundClaim({
        variables: { input: payload },
      });
    };

    const handleStoreCreditClaim = (input: any) => {
      let payload = {
        ...input,
        storeId,
        id: params.claimId,
        items: selectedClaimItems
          .map((item) => ({
            id: item.id,
            quantity: item.quantity,
          }))
          .filter((item) => item.quantity), // Filter items with 0 quantity,
      };
      if (!payload.items.length) {
        showNotification(NotificationTypes.error, 'Invalid Item Quantity');
      }
      storeCreditClaim({
        variables: { input: payload },
      });
    };

    const handleDenyClaim = (input: any) => {
      let payload = {
        ...input,
        storeId,
        id: params.claimId,
        items: selectedClaimItems
          .map((item) => ({
            id: item.id,
            quantity: item.quantity,
          }))
          .filter((item) => item.quantity),
      };
      denyClaim({
        variables: { input: payload },
      });
    };

    const handleRemoveClaimImage = (id: string) => {
      removeClaimImage({
        variables: {
          id,
        },
      });
    };

    const handleRemoveClaimVideo = (id: string) => {
      removeClaimVideo({
        variables: {
          id,
        },
      });
    };

    const handleShowImage = (image: any) => {
      setImage(image);
    };

    const handleShowVideo = (video: string) => {
      setVideo(video);
    };

    const handleImageModal = (value: any) => {
      setShowImageModal(value);
      if (!value) {
        setImage(null);
      }
    };

    const handleImageDownload = (imgUrl: string) => {
      fetch(imgUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'image/png',
        },
      })
        .then((response) => response.blob())
        .then((blob) => {
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `issue-${claim?.number}_image.png`);

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          link?.parentNode?.removeChild(link);
        });
    };

    if (loading) return <LoadingScreen />;

    const renderEmptyTrackingComponent = () => {
      return <Text value={'N/A'} type="body" className="text-gray-500" />;
    };

    const getOrderTrackingLinks = (order: Order | undefined) => {
      const trackingNumbers = getUniqueTrackingNumbers(order?.trackings);
      if (!order?.id || !trackingNumbers.length) {
        return renderEmptyTrackingComponent();
      }
      return trackingNumbers.map((trackingNumber) => (
        <div key={trackingNumber}>
          <TrackingLink
            trackingNumber={trackingNumber}
            ownerType="ORDER"
            ownerId={order.id}
          />
        </div>
      ));
    };

    const onSelectClaimItem = (id: string | undefined) => {
      if (selectedClaimItems.find((si) => si.id === id)) {
        setSelectedClaimItems((items) => items.filter((si) => si.id !== id));
      } else {
        const claimItem = claim?.items.find((c) => c.id === id);

        if (claimItem) {
          setSelectedClaimItems((items) => [...items, claimItem]);
        }
      }
    };

    const onSelectCurrentClaimItem = (id: string) => {
      const item = claim?.items.find((si) => si.id === id);
      if (item) {
        setCurrentClaimItem(item);
      }
    };

    const onSelectAll = () => {
      if (!claim) return;
      const unResolvedItems = claim.items.filter((item) => {
        return !item.settlement;
      });
      setSelectedClaimItems(
        selectedClaimItems.length === unResolvedItems.length
          ? []
          : unResolvedItems || [],
      );
    };

    const onSelectResolution = (r: ClaimItemResolvedStatusEnum) =>
      setResolution(r);

    const settlement = claim?.settlements?.find((s: any) =>
      s.items?.find((si: any) => si?.claimItem?.id === currentClaimItem?.id),
    );

    const onResolvePartialItems = (claimItem: ClaimItemValues) => {
      setResolution(claimItem.resolution as ClaimItemResolvedStatusEnum);
      setResolutionActionModal(true);
      setSelectedClaimItems([claimItem]);
    };

    const onDeleteClaim = () => {
      if (claim) {
        setShowConfirmation({
          title: 'Delete issue',
          message: 'Are you sure you want to delete this issue?',
          subMessage: 'This issue will be permanently deleted.',
          type: NotificationTypes.error,
          onConfirm: () => {
            if (storeId) {
              destroyClaim({
                variables: {
                  input: {
                    storeId,
                    claimId: claim.id,
                  },
                },
              });
            }
          },
          btnText: 'Confirm',
        });
      }
    };

    if (!claim) return null;
    if (!claim.order) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-10">
          <div className="max-w-7xl mx-auto bg-white p-6">
            <Back className="mr-5" returnLocation={returnTo} />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {claim.number}
            </h1>
            <p role="status" className="text-sm text-gray-500 mb-4">
              Order details are unavailable for this issue.
            </p>
            {claim.orderId && (
              <>
                <Link
                  className="inline-block text-sm text-blue-600 hover:underline mb-4"
                  to={withRequestReturnTo(`/orders/${claim.orderId}`, returnTo)}
                >
                  View order
                </Link>
                <OrderRequestsPanel
                  orderId={claim.orderId}
                  currentRequest={{ type: 'ISSUE', id: claim.id }}
                  refreshKey={claim.updatedAt}
                />
              </>
            )}
          </div>
        </div>
      );
    }
    const shopifyOrderUrl = getShopifyOrderAdminUrl(
      storeDomain,
      claim.order.orderId,
    );

    const fulfillmentStatus = getOrderFulfillmentStatus(claim.order?.items);
    const filedDate = claim.createdAt ? new Date(claim.createdAt) : null;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-10">
        <div className="max-w-7xl mx-auto bg-white p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Back className="mr-5" returnLocation={returnTo} />
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  {claim.number}
                </h1>
                {shopifyOrderUrl && (
                  <Link
                    to={shopifyOrderUrl}
                    aria-label="View order in Shopify"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLinkIcon className="w-4 h-4 text-gray-400" />
                  </Link>
                )}
              </div>
              <div className="flex items-start gap-2 flex-col">
                <span
                  className={`px-2 py-1 ${statusColorClasses(
                    claim.status,
                  )} text-sm rounded font-bold`}
                >
                  {formatStatusText(claim.status)}
                </span>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">
                    Issue filed at{' '}
                    {filedDate && !Number.isNaN(filedDate.getTime())
                      ? format(filedDate, 'do MMM yyyy hh:mm')
                      : 'Unavailable'}
                  </span>
                  <span className="text-sm text-gray-500">
                    Issue Filed by {claim?.createdBy?.displayName}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-end gap-2">
                {claim.status === statusType.new &&
                  user.role === userRoles.ADMIN && (
                    <Button
                      props={{
                        'aria-label': 'Delete issue',
                      }}
                      className="bg-red-600 hover:bg-red-700"
                      onClick={onDeleteClaim}
                    >
                      Delete issue
                    </Button>
                  )}
              </div>
            </div>
          </div>

          <OrderRequestsPanel
            orderId={claim.orderId}
            currentRequest={{ type: 'ISSUE', id: claim.id }}
            refreshKey={claim.updatedAt}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left */}
            <div className="lg:col-span-2 space-y-6">
              <ClaimItemsList
                claim={claim}
                items={selectedClaimItems}
                resolution={resolution}
                onSelectAll={onSelectAll}
                onSelectClaimItem={onSelectClaimItem}
                onSelectResolution={onSelectResolution}
                onShowResolutionAction={() => setResolutionActionModal(true)}
                onViewResolution={(id) => {
                  onSelectCurrentClaimItem(id);
                  setResolutionViewModal(true);
                }}
                onResolvePartialItems={onResolvePartialItems}
              />
              {/* Issue Images */}
              <div className="border rounded-lg p-3">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Issue Images
                </h3>

                <div className="flex gap-3 mb-4">
                  {claim && ['DENIED', 'RESOLVED'].includes(claim.status) && (
                    <div className="flex flex-wrap">
                      {claim.images.length > 0 ? (
                        claim.images.map((image) => {
                          return (
                            <img
                              key={image.url}
                              src={image.url}
                              alt="Issue item"
                              onClick={() => handleShowImage(image.url)}
                              style={{
                                width: 125,
                                height: 125,
                                objectFit: 'cover',
                                background: 'rgb(231 231 231)',
                                border: '5px solid rgb(231 231 231 / 75%)',
                                borderRadius: '5px',
                              }}
                              className="mr-2 mb-3 sm:mb-0 cursor-pointer"
                            />
                          );
                        })
                      ) : (
                        <div>No Images added to this issue</div>
                      )}
                    </div>
                  )}

                  {claim && !resolvedStatuses.includes(claim?.status) && (
                    <div>
                      <ClaimsImagesUpload
                        onUpload={({ urls }: { urls: string[] }) => {
                          setImageUrls([...imageUrls, ...urls]);
                          handleAddClaimImages(urls);
                        }}
                        noOfImages={5 - claim.images.length}
                        claimImages={claim.images}
                        onRemoveImage={handleRemoveClaimImage}
                        onShowImage={handleShowImage}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Issue Images */}
              <div className="border rounded-lg p-3">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Issue Videos
                </h3>

                <div className="flex gap-3 mb-4">
                  {claim && ['DENIED', 'RESOLVED'].includes(claim.status) && (
                    <div className="flex flex-wrap">
                      {claim.videos.length > 0 ? (
                        claim.videos.map((video) => {
                          return (
                            <div
                              key={video.id}
                              onClick={handleShowVideo?.bind(null, video.url)}
                              className="h-24 w-24 relative sm:h-24 sm:w-24 cursor-pointer mr-10 mb-3 sm:mb-0"
                            >
                              <img
                                src={getThumnailUrl(video.url)}
                                alt="Video Thumbnail"
                                style={{
                                  maxWidth: 125,
                                  height: 125,
                                  objectFit: 'cover',
                                  background: 'rgb(231 231 231)',
                                  border: '5px solid rgb(231 231 231 / 75%)',
                                  borderRadius: '5px',
                                }}
                                className="cursor-pointer"
                              />
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 64 64"
                                style={{
                                  position: 'absolute',
                                  top: '65%',
                                  left: '65%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '35px',
                                  height: '35px',
                                  opacity: 0.7,
                                  cursor: 'pointer',
                                }}
                              >
                                <circle
                                  cx="32"
                                  cy="32"
                                  r="32"
                                  fill="rgba(0, 0, 0, 0.5)"
                                />
                                <polygon
                                  points="24,16 48,32 24,48"
                                  fill="white"
                                />
                              </svg>
                            </div>
                          );
                        })
                      ) : (
                        <div>No Videos added to this issue</div>
                      )}
                    </div>
                  )}

                  {claim && !resolvedStatuses.includes(claim?.status) && (
                    <div>
                      <ClaimsVideosUpload
                        onUpload={({ urls }: { urls: string[] }) => {
                          setVideoUrls([...videoUrls, ...urls]);
                          handleAddClaimVideos(urls);
                        }}
                        noOfVideos={5 - claim.videos.length}
                        videos={claim.videos}
                        onRemoveVideo={handleRemoveClaimVideo}
                        onShowVideo={handleShowVideo}
                      />
                    </div>
                  )}
                </div>
              </div>
              {claim?.activities?.length > 0 && (
                <ActivitiesTimeline
                  activities={claim.activities}
                  title="Issue activity"
                />
              )}
            </div>

            {/* Sidebar - Right */}
            <div className="space-y-6">
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Risk Analysis</h3>
                  <UserIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                </div>

                <div className="space-y-3">
                  {claim?.orderId && (
                    <OrderRiskAnalysisResult
                      orderId={claim.orderId}
                      flex="col"
                    />
                  )}
                </div>
              </div>
              {/* Customer */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Customer</h3>
                  <UserIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                </div>

                <div className="space-y-3">
                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Number
                    </div>
                    <Link
                      className="text-sm text-blue-600 hover:underline"
                      to={withRequestReturnTo(
                        `/orders/${claim.orderId}`,
                        returnTo,
                      )}
                    >
                      {claim?.order.number}
                    </Link>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Date
                    </div>
                    <div className="text-sm text-gray-500">
                      {claim
                        ? format(
                            new Date(claim?.order?.createdAt),
                            'do MMM yyyy hh:mm',
                          )
                        : ''}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Customer Name
                    </div>
                    <div className="text-sm text-gray-500">
                      {claim?.order.customerName}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Email
                    </div>
                    <div className="text-sm text-gray-500">
                      {claim?.order.customerEmail ?? 'N/A'}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Phone
                    </div>
                    <div className="text-sm text-gray-500">
                      {claim?.order.customerPhone ?? 'N/A'}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Total
                    </div>
                    <div className="text-sm text-gray-500">
                      {getStoreCurrencyFormatter(
                        storeProperties?.currency,
                        formatCurrency(claim?.order.total ?? 0),
                      )}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Guarantee Total
                    </div>
                    <div className="text-sm text-gray-500">
                      {getStoreCurrencyFormatter(
                        storeProperties?.currency,
                        claim?.order.protectionTotal,
                      )}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      Fulfillment Status
                    </div>
                    <StatusLabel
                      label={fulfillmentStatus}
                      type={getOrderFulfillmentStatusType(fulfillmentStatus)}
                    />
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Tracking
                    </div>
                    <div className="text-sm text-gray-500">
                      {getOrderTrackingLinks(claim?.order)}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Customer Notes
                    </div>
                    <div className="text-sm text-gray-500">
                      {Boolean(claim?.customerNotes)
                        ? claim?.customerNotes
                        : 'N/A'}
                    </div>
                  </div>

                  {storeProperties?.resolvePortalOptions
                    ?.requirePreferredResolution && (
                    <div className="border-b border-gray-200 py-2">
                      <div className="text-sm font-semibold text-gray-700">
                        Preferred Resolution
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatStatusText(claim?.preferredResolution) ?? 'N/A'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {claim && (
          <>
            <Modal
              open={showImageModal}
              openModal={handleImageModal}
              containerClass="bg-transparent shadow-none max-w-2xl"
              iconClassName="text-white"
            >
              {image && (
                <img
                  src={image}
                  alt="Issue attachment"
                  className="max-w-full"
                />
              )}
              {image && (
                <div
                  className="flex w-24 rounded p-2 bg-gray-800 z-10 items-center p-1 cursor-pointer"
                  onClick={() => handleImageDownload(image)}
                >
                  <DownloadIcon height={15} width={15} color="#ffffff" />
                  <Text
                    className="text-gray-200 ml-2 text-sm"
                    value="Download"
                  />
                </div>
              )}
            </Modal>
            {video && (
              <ViewVideo videoUrl={video} onCloseView={() => setVideo(null)} />
            )}
            <Modal
              open={resolutionActionModal}
              openModal={() => setResolutionActionModal(false)}
              customStyle={{ width: '80%' }}
            >
              <div className="flex justify-between items-center flex-wrap">
                <ClaimResolutionModalHeader
                  claim={claim}
                  resolution={resolution}
                  storeProperties={storeProperties}
                />
                <Dropdown
                  placeholder="Choose Resolution"
                  btnClassName="w-40 border rounded-sm"
                  options={ClaimResolutionOptions({ enableStoreCredit })}
                  onSelectOption={(o) => {
                    onSelectResolution(o as ClaimItemResolvedStatusEnum);
                  }}
                />
              </div>

              <div>
                <div>
                  {resolution === ClaimItemResolvedStatusEnum.REORDER && (
                    <Reorder
                      claimItems={selectedClaimItems}
                      handleRemoveItem={(item) => handleRemoveClaimItem(item)}
                      handleReorderItems={handleReorderClaim}
                      loading={reorderClaimLoading}
                      claim={claim}
                      handleQuantityUpdate={handleQuantityUpdate}
                      staticClaimItems={
                        refetchData?.claims[0]?.items ?? data?.claims[0]?.items
                      }
                    />
                  )}

                  {resolution === ClaimItemResolvedStatusEnum.REFUND && (
                    <Refund
                      claimItems={selectedClaimItems}
                      handleRemoveItem={(item) => handleRemoveClaimItem(item)}
                      handleRefundItems={handleRefundClaim}
                      loading={refundClaimLoading}
                      claim={claim}
                      handleQuantityUpdate={handleQuantityUpdate}
                      staticClaimItems={
                        refetchData?.claims[0]?.items ?? data?.claims[0]?.items
                      }
                    />
                  )}
                  {resolution === ClaimItemResolvedStatusEnum.STORE_CREDIT && (
                    <StoreCredit
                      claimItems={selectedClaimItems}
                      handleRemoveItem={(item) => handleRemoveClaimItem(item)}
                      handleStoreCreditItems={handleStoreCreditClaim}
                      loading={storeCreditClaimLoading}
                      claim={claim}
                      handleQuantityUpdate={handleQuantityUpdate}
                      staticClaimItems={
                        refetchData?.claims[0]?.items ?? data?.claims[0]?.items
                      }
                    />
                  )}
                  {resolution === ClaimItemResolvedStatusEnum.OTHER && (
                    <Other
                      claimItems={selectedClaimItems}
                      handleRemoveItem={(item) => handleRemoveClaimItem(item)}
                      handleOtherItems={handleOtherClaims}
                      loading={otherClaimLoading}
                      claim={claim}
                      handleQuantityUpdate={handleQuantityUpdate}
                      staticClaimItems={
                        refetchData?.claims[0]?.items ?? data?.claims[0]?.items
                      }
                    />
                  )}

                  {resolution === ClaimItemResolvedStatusEnum.DENY && (
                    <Denyclaim
                      claimItems={selectedClaimItems}
                      staticClaimItems={
                        refetchData?.claims[0]?.items ?? data?.claims[0]?.items
                      }
                      handleRemoveItem={(item) => handleRemoveClaimItem(item)}
                      handleQuantityUpdate={handleQuantityUpdate}
                      handleDenyItems={handleDenyClaim}
                      loading={denyClaimLoading}
                    />
                  )}
                </div>
              </div>
            </Modal>

            <Modal
              open={resolutionViewModal}
              openModal={() => setResolutionViewModal(false)}
            >
              <ClaimResolutionModalHeader
                claim={claim}
                claimItem={currentClaimItem}
                storeProperties={storeProperties}
              />
              <div>
                {currentClaimItem?.resolution === 'REFUND' && (
                  <ResolutionRefund
                    resolution={settlement}
                    key={settlement.id}
                    claim={{ ...claim, items: [currentClaimItem] }}
                    claimItem={currentClaimItem}
                  />
                )}
                {currentClaimItem?.resolution === 'STORE_CREDIT' && (
                  <ResolutionStoreCredit
                    resolution={settlement}
                    key={settlement.id}
                    claim={{ ...claim, items: [currentClaimItem] }}
                    claimItem={currentClaimItem}
                  />
                )}
                {currentClaimItem?.resolution === 'DENY' && (
                  <ResolutionDeny
                    resolution={settlement}
                    key={settlement.id}
                    claim={{ ...claim, items: [currentClaimItem] }}
                    claimItem={currentClaimItem}
                  />
                )}
                {currentClaimItem?.resolution === 'REORDER' && (
                  <ResolutionReorder
                    resolution={settlement}
                    key={settlement.id}
                    claim={{ ...claim, items: [currentClaimItem] }}
                    claimItem={currentClaimItem}
                  />
                )}
                {currentClaimItem?.resolution === 'OTHER' && (
                  <ResolutionOther
                    resolution={settlement}
                    key={settlement.id}
                    claim={{ ...claim, items: [currentClaimItem] }}
                    claimItem={currentClaimItem}
                  />
                )}
              </div>
            </Modal>
          </>
        )}
      </div>
    );
  }),
);
